import { api } from './api';
import type { ApiResponse } from './api';

/**
 * 공지의 web 쪽 api 클라이언트 — **게시판(보존) · 고정 띠(노출) · 읽음** + **조직 저작 CRUD**
 * (줄기 `popup-and-announcement-surface` P4·P4-b).
 *
 * ⚠️ **`lib/api.ts`에 넣지 않는다** — 그 파일은 공용 axios 인스턴스·토큰 관리의 자리이고 도메인별
 * 호출은 `lib/popups.ts`·`lib/notifications.ts`처럼 별 모듈이 관례다(그 파일 JSDoc).
 *
 * ## 뷰어 라우트가 **둘로 갈린 것이 서버의 설계**다 — 여기서 합치지 말 것
 *
 * `GET /announcements`(게시판)는 **지난 공지도** 돌려주고 `GET /announcements/pinned`(띠)는 기간
 * 안인 것만 돌려준다. 한 함수에 `includeExpired` 같은 플래그로 담으면 **플래그 하나가 두 뜻을 지고
 * 노출 판정이 조용히 느슨해지는데 그 느슨해짐에는 red가 없다**(`announcements.service.ts` 헤더
 * JSDoc이 정본이고, 줄기 P4-b가 이 자리를 이름으로 경고한다).
 *
 * ## 조직 식별자를 **뷰어 호출에 싣지 않는다**
 *
 * 서버가 스코프를 `request.user.memberships`에서 파생하므로 조직 param·쿼리를 받지 않는다 —
 * 근거 전문은 `announcements-viewer.controller.ts` JSDoc이다. 조직 id가 들어가는 것은 **저작
 * 경로뿐**이고 그쪽은 `OrganizationGuard`가 param만 신뢰한다.
 */

/** `GET /announcements`·`/announcements/pinned` 한 건 — 서버 `AnnouncementBoardItem`과 같은 형상 */
export interface AnnouncementBoardItem {
  id: string;
  /** null = 전사 공지(슈퍼관리자가 만든 것) · 값 있으면 그 조직의 공지 */
  organizationId: string | null;
  title: string;
  body: string;
  /** 고정 띠 후보인가 — **상한 2·접힘은 화면이 적용한다**(결정 2026-08-19) */
  pinned: boolean;
  publishedAt: string;
  startsAt: string | null;
  endsAt: string | null;
  /** 「읽지 않음」 배지 — 봤음이 없거나 본문 개정(`contentRevisedAt`)보다 오래됐으면 참 */
  unread: boolean;
  /** 노출 기간이 지났나 — **목록에는 남는다**(보존 축). 화면이 「지난 공지」로 구분해 그린다 */
  expired: boolean;
}

/** 게시판 — 전사 + 내 조직 공지가 한 응답에 섞여 온다(구분은 `organizationId`) */
export async function fetchAnnouncementBoard(): Promise<AnnouncementBoardItem[]> {
  const res = await api.get<ApiResponse<AnnouncementBoardItem[]>>('/announcements');
  return res.data.data ?? [];
}

/** 고정 띠 후보 — 기간 안 ∧ `pinned`. **봤음으로 거르지 않는다**(「고정」의 뜻이 「계속 보인다」다) */
export async function fetchPinnedAnnouncements(): Promise<AnnouncementBoardItem[]> {
  const res = await api.get<ApiResponse<AnnouncementBoardItem[]>>('/announcements/pinned');
  return res.data.data ?? [];
}

/**
 * 「읽음」 — 계정 단위 봤음을 남긴다(기기가 바뀌어도 유지된다).
 *
 * ⚠️ body를 **`{}`로 보낸다** — `null`을 명시하면 일부 환경에서 CORS preflight가 깨져
 * `ERR_NETWORK`가 된다(`memory/feedback-axios-post-null-body-cors`).
 */
export async function markAnnouncementSeen(announcementId: string): Promise<void> {
  await api.post(`/announcements/${announcementId}/seen`, {});
}

// ── 조직 저작 ─────────────────────────────────────────────────────────────────

/**
 * 이 화면이 **보낼 수 있는** surface — 조직 공지는 `ADMIN`을 담을 수 없다.
 *
 * 서버가 `ANNOUNCEMENT_ADMIN_PLATFORM_NOT_ALLOWED`로 거부하고(그 축의 소유자는 server
 * `isCompanyViewerPlatform`과 `announcements.controller.ts`의 400 목록), 그 값을 고를 수 있게 두면
 * 저작자가 **거부되는 조합을 만들 수 있다.** ⚠️ 서버 enum의 **사본이 아니다** — 「조직 뷰어가 보는
 * surface」라는 **더 좁은 집합**이고, 서버에 네 번째 뷰어 surface가 생기면 그때 함께 넓힌다.
 */
export const ORG_ANNOUNCEMENT_PLATFORMS = ['WEB', 'APP'] as const;
export type OrgAnnouncementPlatform = (typeof ORG_ANNOUNCEMENT_PLATFORMS)[number];

/**
 * `GET /organizations/:id/announcements` 한 건 — **초안·숨김·만료를 포함한 저작 화면의 행**이다.
 *
 * 서버가 Prisma `Announcement`를 그대로 돌려주므로 뷰어 계약(`AnnouncementBoardItem`)과 **칸이
 * 다르다** — 이쪽에만 `hidden`·`publishedAt: null`(초안)이 있고, 반대로 `unread`·`expired` 같은
 * **파생 칸이 없다**(그 둘은 뷰어 서비스가 계산한다). 두 타입을 합치지 말 것.
 *
 * ⚠️ `platforms`를 `string[]`로 받는다 — **와이어 값이 실제로 열려 있다**(서버 enum에 값이 더해지면
 * 옛 번들이 그것을 받는다). 좁은 union으로 읽으면 그 창에서 **타입이 거짓**이 된다. 쓰기 쪽만
 * `OrgAnnouncementPlatform`으로 좁힌다(같은 자세: `lib/auth.ts`의 `verificationStatus`).
 */
export interface OrgAnnouncement {
  id: string;
  organizationId: string | null;
  title: string;
  body: string;
  platforms: string[];
  audiences: string[];
  pinned: boolean;
  hidden: boolean;
  /** null = 초안(게시 전) — 목록·띠에 들지 않는다 */
  publishedAt: string | null;
  startsAt: string | null;
  endsAt: string | null;
  createdAt: string;
  /** 병행 팝업 id — null이면 「팝업으로도 알리기」가 꺼져 있다 */
  popupId: string | null;
  /**
   * 병행 팝업의 두 옵션 — 저작 목록이 함께 싣는다(서버 `AUTHORING_POPUP_INCLUDE`). 수정 폼이 토글을
   * 지금 값으로 채운다. `string`인 이유는 `platforms`와 같다(와이어 값이 열려 있다).
   */
  popup?: { frequency: string; optOut: string } | null;
}

/**
 * 「팝업으로도 알리기」의 두 옵션 — 서버 `AnnouncementPopupOptionsDto`와 같은 칸이다.
 * 값 목록과 화면 문구는 `pages/settings/announcements/announcementPopupOptions.ts`가 소유한다
 * (이 모듈을 `vi.mock`하는 spec이 있어 **런타임 값을 여기 두지 않는다**).
 */
export interface AnnouncementPopupOptionsInput {
  frequency: 'ONCE' | 'EVERY_VISIT';
  optOut: 'NONE' | 'HIDE_TODAY' | 'NEVER_AGAIN';
}

/**
 * 조직 공지 생성·수정 body.
 *
 * ⚠️ `organizationId`·`orgScope`·`targetOrganizationIds`를 **담지 않는다** — 조직 DTO가 그 칸을
 * 받지 않아 `forbidNonWhitelisted`가 400을 낸다(대상은 URL param이 정한다). 그 금지의 근거는
 * `announcements.controller.ts`·`dto/announcement.dto.ts` JSDoc이 정본이다.
 *
 * ⚠️ `audiences`를 담지 않는 것은 **이 라운드의 범위**다 — 생략하면 「그 surface의 모두」이고(서버
 * DTO 규약) 조직 공지는 그 조직 전체가 대상이다. 역할 좁히기를 UI로 열려면 `acknowledge…` 축까지
 * 함께 와야 하므로(그 DTO의 「좁히지 않은 체계」 거부) 별 작업으로 둔다.
 */
export interface OrgAnnouncementInput {
  title: string;
  body: string;
  platforms: OrgAnnouncementPlatform[];
  pinned?: boolean;
  /**
   * ISO 8601 UTC. 생성에서 생략 = 하한 없음. **수정에서 생략은 「유지」이고 `null`이 「지움」이다**
   * (서버 `UpdateAnnouncementDto.startsAt`가 `string | null`) — 수정 폼에서 칸을 비우면 `null`을 보낸다.
   */
  startsAt?: string | null;
  /** ISO 8601 UTC. 생략·`null`의 뜻은 `startsAt`과 같다. 지나도 **게시판에는 남는다** */
  endsAt?: string | null;
  /** true = 즉시 게시 · false·생략 = 초안 */
  publish?: boolean;
  /** 수정에서만 뜻이 있다 — 작성자가 내려 두는 상태(기간·게시와 독립) */
  hidden?: boolean;
  /**
   * 「팝업으로도 알리기」 — 객체면 켠다. **수정에서 `null`은 끈다**(연결 팝업이 지워진다) · 생략은
   * 유지다. 생성에서는 끈 상태를 **생략**으로 보낸다(서버 DTO JSDoc의 세 갈래).
   */
  popup?: AnnouncementPopupOptionsInput | null;
}

const orgBase = (organizationId: string) => `/organizations/${organizationId}/announcements`;

export async function fetchOrgAnnouncements(organizationId: string): Promise<OrgAnnouncement[]> {
  const res = await api.get<ApiResponse<OrgAnnouncement[]>>(orgBase(organizationId));
  return res.data.data ?? [];
}

export async function createOrgAnnouncement(
  organizationId: string,
  input: OrgAnnouncementInput,
): Promise<OrgAnnouncement> {
  const res = await api.post<ApiResponse<OrgAnnouncement>>(orgBase(organizationId), input);
  return res.data.data!;
}

export async function updateOrgAnnouncement(
  organizationId: string,
  announcementId: string,
  input: Partial<OrgAnnouncementInput>,
): Promise<OrgAnnouncement> {
  const res = await api.patch<ApiResponse<OrgAnnouncement>>(
    `${orgBase(organizationId)}/${announcementId}`,
    input,
  );
  return res.data.data!;
}

export async function deleteOrgAnnouncement(
  organizationId: string,
  announcementId: string,
): Promise<void> {
  await api.delete(`${orgBase(organizationId)}/${announcementId}`);
}
